<template>
  <lazy-component class="star-resource-button-wrapper" @intersected="onVisibleInViewport">
    <button class="star-resource-button" :class="{ unstar: isStarred }" @click.prevent="toggleStar">
      <cv-tooltip
        v-if="!inDropdown"
        class="star-resource-tooltip"
        :alignment="tipAlignment"
        direction="bottom"
        :tip="tipLabel"
      >
        <svg-icon :name="starIcon" kind="action" />
      </cv-tooltip>
      <svg-icon v-if="inDropdown" :name="starIcon" kind="action" />
      <div v-if="inDropdown" class="star-resource-button-label">{{ getStarResourceLabel }}</div>
    </button>
  </lazy-component>
</template>

<script setup lang="ts">
import StarResourceApi from "@apis/star.resource.api";
import { isEmpty } from "@utils/var.check";
import { RESOURCE_STATUS } from "@modules/editor/service/editor.constant";
import StarResourceEventBus from "@plugins/common/service/star.resource.event.bus.ts";
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "@plugins/toast/composables/toast-instance.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import { useEventBusWatch } from "@plugins/common/composables/event-bus-watch.ts";
import { useRouter } from "vue-router";

// Props
interface Props {
  resourceUuid: string;
  resourceType: string;
  resourceStatus?: string;
  tipAlignment?: string;
  inDropdown?: boolean;
}

const {
  resourceUuid,
  resourceType,
  resourceStatus = RESOURCE_STATUS.APPROVED,
  tipAlignment = "center",
  inDropdown = false,
} = defineProps<Props>();

// Emits
const emit = defineEmits<{
  clickEvent: [data: unknown];
  resourceUnstarred: [unstarred: boolean];
}>();

// Composables
const { t } = useI18n();
const toast = useToast();
const { passiveApiFailHandler, actionApiFailHandler } = useErrorHandler();
const { trackWatch } = useEventBusWatch();
const router = useRouter();

// Data
const isStarred = ref(false);
const starredResourceId = ref(null);
const isFavoriteProcessing = ref(false);

// Computed
const starIcon = computed(() => {
  return isStarred.value ? "Unstar24" : "Star24";
});

const errorMessageCaption = computed(() => {
  const starredResourceRoute = router.resolve({
    name: "StarredResource",
  });

  return t("star_resource_error_message_caption", {
    manageStarredResourceLink: starredResourceRoute.href,
  });
});

const tipLabel = computed(() => {
  return isStarred.value ? t("star_resource_unstar_label") : t("star_resource_star_label");
});

const getStarResourceLabel = computed(() => {
  return isStarred.value
    ? t("lesson_button_unstar_resource_dropdown_text")
    : t("lesson_button_star_resource_dropdown_text");
});

// Methods
const onVisibleInViewport = () => {
  getStarred();
};

const toggleStar = (event) => {
  emit("clickEvent", event);
  if (isFavoriteProcessing.value) return;
  isFavoriteProcessing.value = true;
  if (isStarred.value) {
    unstarResource();
  } else {
    starResource();
  }
};

const unstarResource = () => {
  StarResourceApi.unstarResource(starredResourceId.value).then(
    (response) => {
      if (response.code !== "ok") {
        actionApiFailHandler();
      } else {
        isStarred.value = false;
        emit("resourceUnstarred", true);
        StarResourceEventBus.$emit("unstarResource", { resourceUuid: resourceUuid });
      }
      isFavoriteProcessing.value = false;
    },
    () => {
      actionApiFailHandler();
      isFavoriteProcessing.value = false;
    },
  );
};

const starResource = () => {
  StarResourceApi.starResource(resourceUuid, resourceType, resourceStatus).then(
    (response) => {
      if (response.code !== "ok") {
        const errorCode = !isEmpty(response.errorMap) ? response.errorMap[0] : "";
        if (errorCode === "resource.favourite.exceed.limit") {
          showMaxStarredToast();
        } else {
          actionApiFailHandler();
        }
      } else {
        isStarred.value = true;
        emit("resourceUnstarred", false);
        starredResourceId.value = response.data.id;
        StarResourceEventBus.$emit("starResource", {
          resourceUuid: resourceUuid,
          starredResourceId: starredResourceId.value,
        });
      }
      isFavoriteProcessing.value = false;
    },
    () => {
      actionApiFailHandler();
      isFavoriteProcessing.value = false;
    },
  );
};

const showMaxStarredToast = () => {
  toast.show("error", {
    title: t("star_resource_error_message_title"),
    caption: errorMessageCaption.value,
    hideCloseButton: false,
    hasTimeout: false,
    sanitizeCaption: false,
  });
};

const getStarred = () => {
  // Do not set star is resourceUuid is not defined or null (Due to issue creating components)
  if (!resourceUuid) {
    isStarred.value = false;
    return;
  }
  StarResourceApi.isStarred(resourceUuid, resourceType, resourceStatus).then((response) => {
    if (response.data && response.data.id) {
      starredResourceId.value = response.data.id;
      isStarred.value = true;
    } else {
      isStarred.value = false;
    }
  }, passiveApiFailHandler);
};

// Watch
watch(
  () => resourceUuid,
  () => {
    getStarred();
  },
);

// Mounted
onMounted(() => {
  trackWatch(
    StarResourceEventBus.$on("unstarResource", (data) => {
      if (resourceUuid === data.resourceUuid) {
        isStarred.value = false;
      }
    }),
  );

  trackWatch(
    StarResourceEventBus.$on("starResource", (data) => {
      if (resourceUuid === data.resourceUuid) {
        isStarred.value = true;
        starredResourceId.value = data.starredResourceId;
      }
    }),
  );
});
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";

.star-resource-button {
  display: flex;
  align-items: center;
  cursor: pointer;

  .bx--tooltip__trigger {
    width: 24px;
    height: 24px;
  }

  .bx--tooltip__trigger {
    &:focus {
      svg {
        outline: none;
      }
    }

    svg {
      fill: none;
    }
  }
}
</style>
